import type { MetaOnboardingPageView } from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type KeyboardEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { adminKeys, connectPage, fetchOnboarding, onboardingFailureText } from '../../lib/admin';
import { errorText } from '../../lib/copy';
import { formatTime } from '../../lib/time';
import { useCurrentUser } from '../../lib/session';
import { IconAlert, IconBack, IconPlug } from '../../ui/icons';
import { AdminShell } from './AdminShell';
import styles from './Connections.module.css';
import { actionErrorText } from './ConnectionsPage';
import { nextPage, pageProblem, selectedPage } from './page-selection';

function instagramText(page: MetaOnboardingPageView): string {
  switch (page.instagram) {
    case 'AVAILABLE': return 'Messenger e Instagram de su cuenta profesional vinculada.';
    case 'NOT_LINKED': return 'Solo Messenger: la página no tiene una cuenta profesional de Instagram vinculada.';
    case 'NOT_AUTHORIZED': return 'Solo Messenger: no se concedieron los permisos de Instagram.';
  }
}

/** Vuelta del diálogo de Meta: la persona elige cuál de las páginas autorizadas conecta Hilo. */
export function OnboardingPage() {
  const { id = '' } = useParams();
  const user = useCurrentUser();
  const client = useQueryClient();
  const navigate = useNavigate();
  const onboarding = useQuery({ queryKey: adminKeys.onboarding(id), queryFn: () => fetchOnboarding(id), retry: false, refetchOnWindowFocus: false });
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const view = onboarding.data;
  const pages = view?.pages ?? [];
  const selected = selectedPage(pages, chosen);
  const allConnected = pages.length > 0 && pages.every((page) => page.active);

  const move = (event: KeyboardEvent<HTMLInputElement>) => {
    const next = nextPage(pages, selected, event.key);
    if (!next || busy) return;
    event.preventDefault();
    setChosen(next);
    event.currentTarget.form?.querySelector<HTMLInputElement>(`input[value="${next}"]`)?.focus();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || busy) return;
    setBusy(true);
    setError(null);
    try {
      await connectPage(id, selected);
      await client.invalidateQueries({ queryKey: adminKeys.connections });
      await navigate('/conexiones?alta=hecha', { replace: true });
    } catch (failure) {
      setError(actionErrorText(failure));
      setBusy(false);
      void onboarding.refetch();
    }
  };

  let body;
  if (onboarding.isPending) body = <p className={styles.muted}>Cargando lo que autorizaste…</p>;
  else if (onboarding.isError || !view) body = <p className={styles.muted} role="alert">{errorText(onboarding.error)}</p>;
  else if (view.status !== 'AUTHORIZED') {
    const text = view.status === 'COMPLETED' ? 'Esta autorización ya se usó para conectar una página.'
      : view.status === 'EXPIRED' ? onboardingFailureText('EXPIRED')
        : view.status === 'FAILED' ? onboardingFailureText(view.failureCode) : 'La autorización todavía no terminó en Meta.';
    body = <div className={styles.notice} data-kind="error" role="alert"><span className={styles.noticeIcon} aria-hidden="true"><IconAlert /></span><p>{text}</p></div>;
  } else {
    body = (
      <form className={styles.section} onSubmit={(event) => void submit(event)}>
        <fieldset className={styles.pages}>
          <legend className={styles.sectionTitle}>Páginas que autorizaste</legend>
          {pages.map((page) => {
            const problem = pageProblem(page);
            return (
              <label key={page.pageId} className={styles.page}>
                <input type="radio" name="pagina" value={page.pageId} checked={selected === page.pageId} disabled={problem !== null || busy} aria-disabled={problem !== null || busy}
                  onKeyDown={move} onChange={() => setChosen(page.pageId)} />
                <span className={styles.pageName}>{page.name}</span>
                <span className={styles.pageNote}>{instagramText(page)}{page.connected && !page.active && !page.blocked ? ' Reconectar: la conexión estaba revocada/desactivada.' : ''}</span>
                {problem && <span className={styles.pageWarning}>{problem}</span>}
              </label>
            );
          })}
        </fieldset>
        {error && <div className={styles.notice} data-kind="error" role="alert"><span className={styles.noticeIcon} aria-hidden="true"><IconAlert /></span><p>{error}</p></div>}
        {allConnected && <p className={styles.muted} role="status">Todas las páginas autorizadas ya están conectadas. Vuelve a Conexiones para administrarlas.</p>}
        {!allConnected && <div className={styles.submitRow}>
          <button type="submit" className={`btn btn-primary ${styles.connect}`} disabled={!selected || busy} aria-busy={busy}>
            <IconPlug />{busy ? 'Conectando…' : 'Conectar página'}
          </button>
          <span className={styles.muted}>Tienes hasta las {formatTime(view.expiresAt, user.timezone)} para elegir.</span>
        </div>}
        {!allConnected && <p className={styles.muted}>Hilo suscribe la página a los mensajes de Messenger y crea sus conexiones en pruebas, sin acceso para nadie hasta que lo concedas.</p>}
      </form>
    );
  }

  return (
    <AdminShell>
      <header className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Conexiones · alta con Meta</p>
          <h1 className={styles.title}>Elige la página</h1>
          <p className={styles.lead}>Meta devolvió la autorización a Hilo. Elige qué página conectar; los permisos no pasan por el navegador.</p>
        </div>
        <Link to="/conexiones" className="btn btn-secondary"><IconBack />Volver a Conexiones</Link>
      </header>
      {body}
    </AdminShell>
  );
}
